import { useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { QrCode, Copy, Check, Download as DownloadIcon, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { eventConfig } from "@/config/eventConfig";
import { downloadFile, PANEL_CLASS } from "./dashboardUtils";
import { buildShareMessage, whatsappShareUrl } from "@/lib/shareMessage";

// Always the published domain (never the staging/preview origin).
const SITE = (eventConfig.publicSiteUrl || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
export const adviserLink = (code) => `${SITE}/?ref=${code}`;

const QR_OPTIONS = {
  width: 640,
  margin: 2,
  errorCorrectionLevel: "H",
  color: { dark: "#071524", light: "#FFFFFF" },
};

const QrDialog = ({ adviser, dataUrl, onClose }) => (
  <Dialog open={!!adviser} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="qr-dialog">
      <DialogHeader>
        <DialogTitle>{adviser?.name}</DialogTitle>
        <DialogDescription>
          Scan to open the personalised registration link{adviser?.code ? ` (${adviser.code})` : ""}. Print or share this QR code.
        </DialogDescription>
      </DialogHeader>
      <div className="flex flex-col items-center gap-4 pt-2">
        {dataUrl && (
          <img
            src={dataUrl}
            alt={`QR code for ${adviser?.name}`}
            data-testid="qr-image"
            className="h-56 w-56 rounded-xl border border-[rgba(17,42,67,0.10)] bg-white p-2"
          />
        )}
        <div className="w-full break-all rounded-lg bg-[#FBF8F0] px-3 py-2 text-center text-xs text-[#647384]">
          {adviser ? adviserLink(adviser.code) : ""}
        </div>
        <button
          data-testid="qr-download"
          onClick={() => {
            if (!dataUrl || !adviser) return;
            downloadFile(dataUrl, `cpf-seminar-qr-${adviser.code}.png`);
            toast.success("QR code downloaded.");
          }}
          className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-[#F7B838] text-[#071524] font-semibold hover:bg-[#FFD16F] transition-colors"
        >
          <DownloadIcon size={17} /> Download PNG
        </button>
      </div>
    </DialogContent>
  </Dialog>
);

const ACTION_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[rgba(17,42,67,0.15)] text-sm font-medium text-[#112A43] hover:bg-[#FBF8F0] transition-colors";

export const AdviserLinks = ({ referralCount }) => {
  const [copied, setCopied] = useState("");
  const [qrFor, setQrFor] = useState(null); // { code, name }
  const [qrDataUrl, setQrDataUrl] = useState("");

  const copyLink = async (code) => {
    try {
      await navigator.clipboard.writeText(adviserLink(code));
      setCopied(code);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      toast.error("Copy failed \u2014 select and copy manually.");
    }
  };

  const openQr = async (adviser) => {
    try {
      setQrDataUrl(await QRCode.toDataURL(adviserLink(adviser.code), QR_OPTIONS));
      setQrFor(adviser);
    } catch (e) {
      toast.error("Could not generate QR code. Please try again.");
    }
  };

  return (
    <>
      <div className={PANEL_CLASS}>
        <div className="p-5 border-b border-[rgba(17,42,67,0.08)]">
          <h2 className="text-lg font-bold text-[#122131]">Adviser Links</h2>
          <p className="text-sm text-[#647384] mt-1">
            Each adviser has a personalised link. Registrations that arrive through it are tagged automatically.
          </p>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {(eventConfig.advisers || []).map((a) => (
            <div
              key={a.code}
              data-testid={`adviser-link-${a.code}`}
              className="flex items-center justify-between gap-3 rounded-xl border border-[rgba(17,42,67,0.10)] p-3"
            >
              <div className="min-w-0">
                <div className="font-medium text-[#122131] truncate">
                  {a.name}
                  <span className="ml-2 text-xs font-semibold text-[#126F6A]">{a.code}</span>
                  {referralCount(a.code) > 0 && (
                    <span className="ml-2 text-xs text-[#647384]">· {referralCount(a.code)} reg</span>
                  )}
                </div>
                <div className="text-xs text-[#647384] truncate">{adviserLink(a.code)}</div>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <a
                  data-testid={`share-adviser-${a.code}`}
                  href={whatsappShareUrl(buildShareMessage(a.code))}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Share the video via WhatsApp as ${a.name}`}
                  title="Share the 90-sec video + this link via WhatsApp"
                  className={`${ACTION_BUTTON_CLASS} border-[rgba(31,145,136,0.35)] text-[#126F6A] hover:bg-[rgba(31,145,136,0.08)]`}
                >
                  <MessageCircle size={15} /> <span className="hidden sm:inline">Share</span>
                </a>
                <button
                  data-testid={`qr-adviser-${a.code}`}
                  onClick={() => openQr(a)}
                  aria-label={`Show QR code for ${a.name}`}
                  className={ACTION_BUTTON_CLASS}
                >
                  <QrCode size={15} /> QR
                </button>
                <button data-testid={`copy-adviser-${a.code}`} onClick={() => copyLink(a.code)} className={ACTION_BUTTON_CLASS}>
                  {copied === a.code ? <Check size={15} className="text-[#126F6A]" /> : <Copy size={15} />}
                  {copied === a.code ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <QrDialog adviser={qrFor} dataUrl={qrDataUrl} onClose={() => setQrFor(null)} />
    </>
  );
};
